import { useCallback, useMemo } from "react"
import fp from "lodash/fp"

import type { DateReference, DateReferenceGroup } from "types/date.types"
import type { EntityType } from "types/common.types"
import type { AlertState } from "types/alerts.types"

import { hasForecast } from "helpers/utils/dateUtils"

interface TimeSpan {
  timeAmount: number
  timeUnit: string
}

export interface DateRefOption {
  value: string
  label: string
  time: TimeSpan
  forecastTime?: TimeSpan
  group?: DateReferenceGroup
}

const useSelectOptions = () => {
  const dateRefOptions = useMemo(
    (): DateRefOption[] => [
      {
        value: "triggerWindow",
        time: { timeAmount: 0, timeUnit: "" },
        label: "dates.TRIGGER_WINDOW",
      },
      {
        value: "lastHour",
        group: "hours",
        time: { timeAmount: 1, timeUnit: "hours" },
        label: "dates.LAST_HOUR",
      },
      {
        value: "lastThreeHours",
        group: "hours",
        time: { timeAmount: 3, timeUnit: "hours" },
        label: "dates.LAST_3_HOURS",
      },
      {
        value: "lastThreeHoursPlusOneHour",
        group: "hours",
        time: { timeAmount: 3, timeUnit: "hours" },
        forecastTime: { timeAmount: 1, timeUnit: "hours" },
        label: "dates.LAST_3_HOURS",
      },
      {
        value: "lastTwelveHours",
        group: "hours",
        time: { timeAmount: 12, timeUnit: "hours" },
        label: "dates.LAST_12_HOURS",
      },
      {
        value: "lastTwelveHoursPlusThreeHours",
        group: "hours",
        time: { timeAmount: 12, timeUnit: "hours" },
        forecastTime: { timeAmount: 3, timeUnit: "hours" },
        label: "dates.LAST_12_HOURS",
      },
      {
        value: "lastDay",
        group: "days",
        time: { timeAmount: 1, timeUnit: "days" },
        label: "dates.LAST_DAY",
      },
      {
        value: "lastDayPlusSixHours",
        group: "days",
        time: { timeAmount: 1, timeUnit: "days" },
        forecastTime: { timeAmount: 6, timeUnit: "hours" },
        label: "dates.LAST_DAY",
      },
      {
        value: "lastWeek",
        group: "days",
        time: { timeAmount: 7, timeUnit: "days" },
        label: "dates.LAST_WEEK",
      },
      {
        value: "lastWeekPlusTwoDays",
        group: "days",
        time: { timeAmount: 7, timeUnit: "days" },
        forecastTime: { timeAmount: 2, timeUnit: "days" },
        label: "dates.LAST_WEEK",
      },
      {
        value: "lastMonth",
        group: "longer",
        time: { timeAmount: 1, timeUnit: "months" },
        label: "dates.LAST_MONTH",
      },
      {
        value: "lastQuarter",
        group: "longer",
        time: { timeAmount: 3, timeUnit: "months" },
        label: "dates.LAST_QUARTER",
      },
      {
        value: "lastYear",
        group: "longer",
        time: { timeAmount: 1, timeUnit: "years" },
        label: "dates.LAST_YEAR",
      },
      {
        value: "custom",
        time: { timeAmount: 0, timeUnit: "" },
        label: "dates.CUSTOM",
      },
    ],
    [],
  )

  const noForecastSelectOptions = useMemo(
    () =>
      dateRefOptions.filter((opt) => opt.value !== "triggerWindow" && !opt.forecastTime),
    [dateRefOptions],
  )
  const forecastSelectOptions = useMemo(
    () => dateRefOptions.filter((opt) => opt.value !== "triggerWindow"),
    [dateRefOptions],
  )
  const getSelectOptionsByEntity = useCallback(
    (entity: EntityType) =>
      hasForecast(entity) ? forecastSelectOptions : noForecastSelectOptions,
    [noForecastSelectOptions, forecastSelectOptions],
  )

  const triggeredAccordionSelectOptions = useMemo(
    () => dateRefOptions.filter((opt) => opt.value !== "custom" && !opt.forecastTime),
    [dateRefOptions],
  )
  const notTriggeredAccordionSelectOptions = useMemo(
    () =>
      dateRefOptions.filter(
        (opt) =>
          opt.value !== "custom" && opt.value !== "triggerWindow" && !opt.forecastTime,
      ),
    [dateRefOptions],
  )
  const getSelectOptionsByAlertState = useCallback(
    (alertState: AlertState) => {
      switch (alertState) {
        case "notTriggered":
        case "disabled":
          return notTriggeredAccordionSelectOptions
        default:
          return triggeredAccordionSelectOptions
      }
    },
    [notTriggeredAccordionSelectOptions, triggeredAccordionSelectOptions],
  )

  // nit: Could do with the same format as selectOptions
  const dateReferences: { [key: string]: DateReference } = useMemo(() => {
    const references: DateReference[] = dateRefOptions.map(
      ({ value, time, forecastTime }): DateReference => ({
        name: value,
        timeAmount: time.timeAmount,
        timeUnit: time.timeUnit,
        ...(forecastTime && {
          forecastTimeAmount: forecastTime.timeAmount,
          forecastTimeUnit: forecastTime.timeUnit,
        }),
      }),
    )
    return fp.indexBy(({ name }: DateReference) => name)(references)
  }, [dateRefOptions])

  const noForecastDateReferences = useMemo(
    () =>
      fp.pickBy(
        (dr: DateReference) =>
          dr.name !== "triggerWindow" && !dr.forecastTimeAmount && !dr.forecastTimeUnit,
      )(dateReferences),
    [dateReferences],
  )
  const forecastDateReferences = useMemo(
    () => fp.pickBy((dr: DateReference) => dr.name !== "triggerWindow")(dateReferences),
    [dateReferences],
  )
  const getDateReferencesByEntity = useCallback(
    (entity: EntityType) =>
      hasForecast(entity) ? forecastDateReferences : noForecastDateReferences,
    [noForecastDateReferences, forecastDateReferences],
  )

  const triggeredAccordionDateReferences = useMemo(
    () =>
      fp.pickBy(
        (dr: DateReference) =>
          dr.name !== "custom" && !dr.forecastTimeAmount && !dr.forecastTimeUnit,
      )(dateReferences),
    [dateReferences],
  )
  const notTriggeredAccordionDateReferences = useMemo(
    () =>
      fp.pickBy(
        (dr: DateReference) =>
          dr.name !== "custom" &&
          dr.name !== "triggerWindow" &&
          !dr.forecastTimeAmount &&
          !dr.forecastTimeUnit,
      )(dateReferences),
    [dateReferences],
  )
  const getDateReferencesByAlertState = useCallback(
    (alertState: AlertState) => {
      switch (alertState) {
        case "notTriggered":
        case "disabled":
          return notTriggeredAccordionDateReferences
        default:
          return triggeredAccordionDateReferences
      }
    },
    [notTriggeredAccordionDateReferences, triggeredAccordionDateReferences],
  )

  return {
    dateRefOptions,
    noForecastSelectOptions,
    forecastSelectOptions,
    getSelectOptionsByEntity,
    triggeredAccordionSelectOptions,
    notTriggeredAccordionSelectOptions,
    getSelectOptionsByAlertState,
    dateReferences,
    noForecastDateReferences,
    forecastDateReferences,
    getDateReferencesByEntity,
    triggeredAccordionDateReferences,
    notTriggeredAccordionDateReferences,
    getDateReferencesByAlertState,
  }
}

export default useSelectOptions
